import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import useAffiliateTracker from "@/lib/useAffiliateTracker";
import AdSlot from "@/components/AdSlot";
import SuggestedKits from "@/components/SuggestedKits";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Wrench, Sparkles, Loader2, ArrowRight, Factory, AlertCircle, AlertTriangle, FileText, ExternalLink, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const SITE = "https://spa-report-hub.emergent.host";

const PART_LABELS = {
  pump_1: "Pompe massage 1",
  pump_2: "Pompe massage 2",
  circulation_pump: "Pompe de circulation",
  heater: "Réchauffeur",
  keypad: "Clavier de contrôle",
  controller: "Carte électronique",
  filter_cartridge: "Filtre cartouche",
  blower: "Blower",
  ozonator: "Ozonateur",
};

export default function SpaParts() {
  useAffiliateTracker({ context: "spa_parts_page" });
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [otherMode, setOtherMode] = useState(false);
  const [otherBrandInput, setOtherBrandInput] = useState("");
  const [models, setModels] = useState([]);
  const [modelInput, setModelInput] = useState("");
  const [selectedModel, setSelectedModel] = useState(null);
  const [loadingModels, setLoadingModels] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);

  useSEO({
    title: "Pièces détachées spa par modèle — DoctoSpa",
    description:
      "Trouvez les pièces détachées (pompe, réchauffeur, clavier, filtre) de votre spa en tapant simplement la marque et le modèle. 100+ modèles répertoriés + suggestion IA.",
    url: `${SITE}/pieces-detachees`,
  });

  useEffect(() => {
    api.get("/spa-parts/brands").then((r) => setBrands(r.data.brands || []));
  }, []);

  const loadBrandModels = async (brand) => {
    setSelectedBrand(brand);
    setOtherMode(false);
    setSelectedModel(null);
    setAiSuggestion(null);
    setLoadingModels(true);
    try {
      const r = await api.get("/spa-parts/models", { params: { brand: brand.brand } });
      setModels(r.data.models || []);
    } finally {
      setLoadingModels(false);
    }
  };

  const enterOtherMode = () => {
    setOtherMode(true);
    setSelectedBrand(null);
    setModels([]);
    setSelectedModel(null);
    setAiSuggestion(null);
  };

  const openModel = async (slug) => {
    const r = await api.get(`/spa-parts/models/${slug}`);
    setSelectedModel(r.data);
    setAiSuggestion(null);
    setTimeout(() => document.getElementById("model-detail")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const requestAiSuggest = async () => {
    const brandValue = otherMode ? otherBrandInput.trim() : selectedBrand?.brand;
    if (!brandValue || !modelInput.trim()) {
      toast.error(otherMode
        ? "Saisissez la marque et le modèle de votre spa."
        : "Choisissez d'abord une marque et saisissez un modèle.");
      return;
    }
    setAiLoading(true);
    setAiSuggestion(null);
    try {
      const r = await api.post("/spa-parts/ai-suggest", {
        brand: brandValue,
        model_name: modelInput.trim(),
      });
      setAiSuggestion(r.data);
      setSelectedModel(null);
      setTimeout(() => document.getElementById("ai-suggestion")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suggestion IA indisponible");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14 lg:py-20" data-testid="spa-parts-page">
      <div className="max-w-3xl">
        <div className="eyebrow">Pièces détachées</div>
        <h1 className="mt-3 font-display text-4xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
          Quelle pièce pour<br />
          <span className="text-primary">votre modèle</span> de spa ?
        </h1>
        <p className="mt-5 text-muted-foreground leading-relaxed">
          Choisissez votre marque, sélectionnez votre modèle : vous obtenez la pompe, le réchauffeur,
          le clavier, la carte, le filtre… avec les références exactes.
        </p>
      </div>

      {/* Step 1: Brand selector */}
      <div className="mt-12" data-testid="brand-selector">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <span className="grid place-items-center h-5 w-5 rounded-full bg-primary text-white text-[10px] font-black">1</span>
          Choisissez la marque
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {brands.map((b) => (
            <button
              key={b.brand_slug}
              onClick={() => loadBrandModels(b)}
              className={`text-sm font-semibold px-4 py-2 rounded-full transition-all ${
                selectedBrand?.brand_slug === b.brand_slug
                  ? "bg-secondary text-white shadow-md"
                  : "bg-black/5 hover:bg-black/10 text-foreground"
              }`}
              data-testid={`brand-btn-${b.brand_slug}`}
            >
              {b.brand} <span className="opacity-60 text-xs">· {b.count}</span>
            </button>
          ))}
          <button
            onClick={enterOtherMode}
            className={`text-sm font-semibold px-4 py-2 rounded-full transition-all border-2 border-dashed ${
              otherMode
                ? "bg-primary text-white border-primary shadow-md"
                : "border-primary/40 text-primary hover:bg-primary/5"
            }`}
            data-testid="brand-btn-autres"
          >
            <Sparkles className="h-3.5 w-3.5 inline mr-1" />
            Autres marques
          </button>
        </div>
      </div>

      {/* Step 2 — Other Mode (custom brand + model with AI suggestion) */}
      {otherMode && (
        <div className="mt-12" data-testid="other-brand-mode">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <span className="grid place-items-center h-5 w-5 rounded-full bg-primary text-white text-[10px] font-black">2</span>
            Marque non listée — recherche via IA
          </div>
          <div className="mt-4 rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-6">
            <div className="flex items-center gap-2 text-sm font-bold text-primary">
              <Sparkles className="h-4 w-4" />
              Recherche libre pour toute marque de spa
            </div>
            <p className="mt-1 text-xs text-muted-foreground max-w-md">
              Saisissez la marque et le modèle exact de votre spa. Notre IA (Claude Sonnet 4.5) analyse la
              configuration et propose les pièces standards en ~3 secondes.
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input
                value={otherBrandInput}
                onChange={(e) => setOtherBrandInput(e.target.value)}
                placeholder="Marque (ex: Villeroy & Boch, Softub, RotoSpa...)"
                data-testid="other-brand-input"
              />
              <Input
                value={modelInput}
                onChange={(e) => setModelInput(e.target.value)}
                placeholder="Modèle (ex: Comfort Line R8L)"
                data-testid="other-model-input"
                onKeyDown={(e) => e.key === "Enter" && requestAiSuggest()}
              />
            </div>
            <Button
              onClick={requestAiSuggest}
              disabled={aiLoading || !otherBrandInput.trim() || !modelInput.trim()}
              className="mt-3 rounded-md h-10 w-full sm:w-auto"
              data-testid="other-ai-suggest-btn"
            >
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {aiLoading ? "Analyse…" : "Suggérer via IA"}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Model selector (when a brand is selected) */}
      {selectedBrand && !otherMode && (
        <div className="mt-12" data-testid="model-selector">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <span className="grid place-items-center h-5 w-5 rounded-full bg-primary text-white text-[10px] font-black">2</span>
            Choisissez votre modèle {selectedBrand.brand}
          </div>

          {loadingModels ? (
            <p className="mt-4 text-sm text-muted-foreground">Chargement…</p>
          ) : (
            <>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="models-grid">
                {models.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => openModel(m.slug)}
                    className={`text-left p-4 rounded-xl border transition-all ${
                      selectedModel?.id === m.id
                        ? "border-primary bg-primary/5"
                        : "border-black/10 hover:border-primary/40 hover:shadow-md"
                    }`}
                    data-testid={`model-btn-${m.brand_slug}-${m.model_name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <div className="font-semibold">{m.model_name}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {m.year_range} {m.seats && `· ${m.seats} places`}
                    </div>
                  </button>
                ))}
              </div>

              {/* AI fallback */}
              <div className="mt-8 rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-6" data-testid="ai-fallback-block">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-bold text-primary">
                      <Sparkles className="h-4 w-4" />
                      Votre modèle n'est pas listé ?
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground max-w-md">
                      Notre IA (Claude Sonnet 4.5) analyse votre modèle et propose une configuration plausible en 3 secondes.
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <Input
                    value={modelInput}
                    onChange={(e) => setModelInput(e.target.value)}
                    placeholder={`Ex : ${selectedBrand.brand} ${models[0]?.model_name || "modèle"}`}
                    className="flex-1"
                    data-testid="ai-model-input"
                    onKeyDown={(e) => e.key === "Enter" && requestAiSuggest()}
                  />
                  <Button
                    onClick={requestAiSuggest}
                    disabled={aiLoading || !modelInput.trim()}
                    className="rounded-md h-10"
                    data-testid="ai-suggest-btn"
                  >
                    {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    {aiLoading ? "Analyse…" : "Suggérer via IA"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 3: Selected model detail */}
      {selectedModel && (
        <div id="model-detail" className="mt-14" data-testid="model-detail">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <span className="grid place-items-center h-5 w-5 rounded-full bg-primary text-white text-[10px] font-black">3</span>
            Pièces standards
          </div>
          <h2 className="mt-3 font-display text-3xl font-black tracking-tight" data-testid="model-detail-title">
            {selectedModel.brand} <span className="text-primary">{selectedModel.model_name}</span>
          </h2>
          {(selectedModel.year_range || selectedModel.seats || selectedModel.dimensions) && (
            <p className="mt-1 text-sm text-muted-foreground">
              {[selectedModel.year_range, selectedModel.seats && `${selectedModel.seats} places`, selectedModel.dimensions]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}

          {selectedModel.notes && (
            <Card className="mt-6 p-4 bg-blue-50 border-blue-200 text-sm text-blue-900" data-testid="model-notes">
              {selectedModel.notes}
            </Card>
          )}

          <PartsGrid model={selectedModel} testidPrefix="part" />

          {/* AD SLOT — after parts grid on model detail */}
          <div className="mt-8">
            <AdSlot placement="model_detail" />
          </div>

          {/* Error codes block */}
          <ErrorCodesBlock model={selectedModel} />

          {/* Technical docs block */}
          <TechnicalDocsBlock model={selectedModel} />

          {/* Kits suggérés — upsell contextuel */}
          <SuggestedKits brand={selectedModel.brand} />

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={`https://www.tubs.fr/fr/advanced_search_result.php?q=${encodeURIComponent(`${selectedModel.brand} ${selectedModel.model_name}`)}&ref=doctospa`}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              data-testid="model-tubs-cta"
              data-track-context="model_detail_cta"
              data-track-model={selectedModel.slug}
            >
              <Button className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90">
                <Wrench className="h-4 w-4 mr-2" /> Toutes les pièces {selectedModel.brand} sur tubs.fr <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </a>
            <span className="text-xs text-muted-foreground">
              💡 Clique sur une pièce ci-dessus pour la chercher directement sur tubs.fr
            </span>
          </div>
        </div>
      )}

      {/* AI suggestion result */}
      {aiSuggestion && (
        <div id="ai-suggestion" className="mt-14" data-testid="ai-suggestion">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary">
            <Sparkles className="h-4 w-4" /> Suggestion IA — à valider par un pro
          </div>
          <h2 className="mt-3 font-display text-3xl font-black tracking-tight">
            {aiSuggestion.brand} <span className="text-primary">{aiSuggestion.model_name}</span>
          </h2>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant={aiSuggestion.confidence === "élevée" ? "default" : "secondary"} className="rounded-full">
              Confiance : {aiSuggestion.confidence}
            </Badge>
            <Badge variant="outline" className="rounded-full">Source : IA (non vérifiée)</Badge>
          </div>

          <Card className="mt-6 p-4 bg-amber-50 border-amber-200 text-sm text-amber-900" data-testid="ai-warning">
            <div className="flex gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <b>Attention :</b> cette configuration est une <b>estimation IA</b>, elle n'a pas été validée par un technicien.
                Vérifiez toujours avec la plaque signalétique de votre spa avant tout achat.
              </div>
            </div>
          </Card>

          {aiSuggestion.reasoning && (
            <Card className="mt-4 p-4 bg-primary/5 border-primary/20 text-sm" data-testid="ai-reasoning">
              <b>Raisonnement IA :</b> {aiSuggestion.reasoning}
            </Card>
          )}

          <PartsGrid model={aiSuggestion.parts} testidPrefix="ai-part" />
        </div>
      )}

      {/* Section explicative */}
      <div className="mt-20 rounded-2xl bg-secondary text-white p-8 lg:p-10">
        <Factory className="h-8 w-8 text-primary" />
        <h3 className="mt-4 font-display text-2xl lg:text-3xl font-bold tracking-tight">
          Comment on construit ce catalogue ?
        </h3>
        <p className="mt-3 text-sm text-white/70 max-w-2xl leading-relaxed">
          100+ modèles pré-remplis manuellement par notre équipe technique à partir des docs constructeurs
          (Balboa, Gecko, Watkins, Waterway, Pleatco, LX Whirlpool, Sundance…). Pour les modèles absents,
          notre IA Claude Sonnet 4.5 propose une configuration probable — les suggestions validées par
          l'admin enrichissent progressivement le catalogue public.
        </p>
      </div>
    </div>
  );
}

function PartsGrid({ model, testidPrefix }) {
  if (!model) return null;
  const rows = Object.keys(PART_LABELS)
    .map((key) => ({ key, label: PART_LABELS[key], value: model[key] }))
    .filter((r) => r.value);

  if (rows.length === 0) {
    return <p className="mt-6 text-sm text-muted-foreground">Aucune pièce répertoriée.</p>;
  }

  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`${testidPrefix}s-grid`}>
      {rows.map((r) => {
        const tubsUrl = r.value.tubs_url;
        const inner = (
          <>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  {r.label}
                </div>
                <div className="mt-1.5 font-semibold text-sm">
                  {r.value.brand || "—"}{" "}
                  {r.value.ref && <span className="text-primary">· {r.value.ref}</span>}
                </div>
                {(r.value.spec || r.value.kw) && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {r.value.spec || `${r.value.kw} kW`}
                  </div>
                )}
                {r.value.notes && (
                  <div className="mt-1.5 text-xs text-muted-foreground italic">{r.value.notes}</div>
                )}
              </div>
              {tubsUrl && (
                <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  Voir sur tubs.fr →
                </span>
              )}
            </div>
          </>
        );

        if (tubsUrl) {
          return (
            <a
              key={r.key}
              href={tubsUrl}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              className="group"
              data-testid={`${testidPrefix}-${r.key}`}
              data-tubs-url={tubsUrl}
              data-track-context="model_detail"
              data-track-part-type={r.key}
              data-track-brand={r.value.brand || ""}
              data-track-part-ref={r.value.ref || ""}
            >
              <Card className="p-4 border-black/10 hover:border-primary/40 hover:shadow-md transition-all cursor-pointer h-full">
                {inner}
              </Card>
            </a>
          );
        }
        return (
          <Card key={r.key} className="p-4 border-black/10" data-testid={`${testidPrefix}-${r.key}`}>
            {inner}
          </Card>
        );
      })}
    </div>
  );
}

const SEVERITY_STYLES = {
  critical: { color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "Critique" },
  warning:  { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", label: "Attention" },
  info:     { color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", label: "Info" },
};

function ErrorCodesBlock({ model }) {
  const codes = model?.error_codes || [];
  if (!codes.length) return null;
  return (
    <div className="mt-10" data-testid="model-error-codes">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        <AlertTriangle className="h-4 w-4" />
        Codes erreurs les plus courants
        {model.platform && (
          <Badge variant="secondary" className="ml-2 rounded-full text-[10px]" data-testid="model-platform-badge">
            Plateforme : {model.platform}
          </Badge>
        )}
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {codes.map((c) => {
          const s = SEVERITY_STYLES[c.severity] || SEVERITY_STYLES.warning;
          return (
            <Link
              key={c.id}
              to={`/codes-erreurs/${c.brand}/${c.code.toLowerCase().replace(/\s+/g, "-")}`}
              data-testid={`error-code-${c.brand}-${c.code.toLowerCase().replace(/\s+/g, "-")}`}
              className="group"
            >
              <Card className={`p-4 border ${s.border} ${s.bg} hover:shadow-md transition-all h-full cursor-pointer`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-mono font-black text-lg ${s.color}`}>{c.code}</span>
                      <Badge variant="outline" className={`text-[10px] rounded-full ${s.color}`}>
                        {s.label}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] rounded-full">
                        {c.brand}
                      </Badge>
                    </div>
                    <div className="mt-1.5 font-semibold text-sm">{c.title}</div>
                    <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      {c.action_summary}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-60 shrink-0 mt-1 transition-opacity" />
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
      <div className="mt-3">
        <Link
          to="/codes-erreurs"
          className="text-xs font-semibold text-primary hover:underline"
          data-testid="all-codes-link"
        >
          Voir tous les codes erreurs →
        </Link>
      </div>
    </div>
  );
}

function TechnicalDocsBlock({ model }) {
  const docs = model?.technical_docs || [];
  if (!docs.length) return null;
  const DOC_TYPE_LABELS = {
    manual: "Manuel",
    troubleshoot: "Dépannage",
    exploded_view: "Vue éclatée",
    schematic: "Schéma",
  };
  return (
    <div className="mt-10" data-testid="model-technical-docs">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        <FileText className="h-4 w-4" />
        Documentation technique
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {docs.map((d) => {
          const isExternal = !!d.external_url;
          const cardInner = (
            <Card className="p-4 border-black/10 hover:border-primary/40 hover:shadow-md transition-all h-full cursor-pointer">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className="text-[10px] rounded-full">
                      {DOC_TYPE_LABELS[d.doc_type] || d.doc_type}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] rounded-full">
                      {d.brand}
                    </Badge>
                  </div>
                  <div className="mt-1.5 font-semibold text-sm">{d.title}</div>
                  {d.summary && (
                    <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{d.summary}</div>
                  )}
                </div>
                {isExternal && <ExternalLink className="h-4 w-4 opacity-60 shrink-0 mt-1" />}
              </div>
            </Card>
          );
          if (isExternal) {
            return (
              <a
                key={d.id}
                href={d.external_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`doc-${d.id}`}
              >
                {cardInner}
              </a>
            );
          }
          return (
            <div key={d.id} data-testid={`doc-${d.id}`}>
              {cardInner}
            </div>
          );
        })}
      </div>
    </div>
  );
}

